import {
    ApiException,
    Booking,
    BookingApi,
    BookingCreate,
    BookingQuotation,
    Configuration,
    InlineResponse2001,
    ListingApi,
    ProviderStatus,
    ReadOnlyBooking,
    ServerConfiguration,
    createConfiguration
} from 'hotdesk_typescript';
import type {
    Middleware,
    RequestContext,
    ResponseContext
} from 'hotdesk_typescript';
import { getRemainingTime, getUnitText } from '@/hotdesk-ui/utils/status';
import { getCookie } from 'cookies-next/client';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import {
    HttpClientLibrary,
    KEEPALIVE_HEADER
} from '@/hotdesk-ui/common-classes/http-client';
import { myPromiseMiddleware } from '@/hotdesk-ui/common-classes/middleware/promise.middleware';

/**
 * One picked service on the quote request. The user-driven picker writes
 * these into `bookingData.selectedServices`; the quote effect threads them
 * into `getProviderBookingQuote`.
 */
export type PickedService = {
    serviceId: string;
    quantity: number;
    notes?: string;
    deliveryTime?: string;
};

/** Per-service line in the quote response (PR #3). */
export type QuotedService = {
    serviceId: string;
    name: string;
    categoryId?: string | null;
    categoryLabel?: string | null;
    quantity: number;
    unitPrice: number;
    currency: string;
    taxAmount: number;
    /**
     * Effective delivery time (BE !1583). When the pick omitted one on a
     * delivery-typed service the BE defaults it to the booking start, so
     * this is the authoritative value — render times from here, never
     * from the local pick.
     */
    deliveryTime?: string | null;
    notes?: string | null;
    /**
     * Non-null when the BE capped this line's quantity at booking
     * duration × seats (BE !1583) — holds the quantity the user asked
     * for; `quantity` above is the effective (billed) value.
     */
    quantityCappedFrom?: number | null;
    /** Echo of the slot row's flag (BE !1630). */
    mustMatchDelegates?: boolean | null;
    /**
     * `true` when the BE rewrote this line's quantity to the booking's
     * seat count because `mustMatchDelegates` is set (BE !1630) —
     * `quantity` above is the effective (billed) value.
     */
    quantityForcedToDelegates?: boolean | null;
};

/** Validation-drop info for picks the BE refused (PR #3 + PR #7). */
export type QuoteServiceDropped = {
    serviceId: string;
    reason: string;
};

/**
 * `servicesDropped[].reason` values that mean "add-ons aren't sellable on
 * this quote at all" rather than "that add-on is wrong" (BE !1630):
 * `services_unavailable_no_hold` — the quote-time hold could not be placed
 * (best-effort, capped), so there is no slot list to sell from; try again.
 * `services_disabled` — the `iwg_services_enabled` kill-switch is off.
 * Both come with an empty `availableServices`, so the picker is hidden
 * rather than showing these as per-row errors.
 */
export const QUOTE_SERVICES_UNAVAILABLE_REASONS: ReadonlySet<string> = new Set([
    'services_unavailable_no_hold',
    'services_disabled'
]);

/** One service line inside `customerBreakdown.services.items`. */
export type CustomerBreakdownItem = {
    serviceId: string;
    name: string;
    quantity: number;
    /** unitPrice × quantity, tax-exclusive, in the breakdown's currency. */
    net: number;
    /** Per-line tax — display `taxTotal` as given, never recompute from these. */
    tax: number;
};

/**
 * `customerBreakdown` — what the customer's total is made of. Provider/IWG
 * only. Appears top-level on `POST /booking/quote/` responses and as
 * `details.customerBreakdown` on booking reads (stamped at create; absent
 * on non-provider bookings and bookings created before the BE shipped it).
 *
 * ALWAYS in the provider's native currency (`currency`), even when the
 * surrounding quote/booking is displayed in another — multiply the money
 * fields by the accompanying `currencyConversionRate` and round to 2dp
 * AFTER converting to show user-currency figures.
 *
 * Invariants (BE-guaranteed): `netTotal = room.net + services.net`;
 * `grossTotal = netTotal + taxTotal`; on quotes `grossTotal` equals the
 * unconverted `totalPrice`. NOTE `taxTotal` can exceed
 * `room.tax + services.tax` (IWG taxes the whole booking in one pass; BE
 * takes the max) — display `taxTotal` as given, never recompute from lines.
 */
export type CustomerBreakdown = {
    currency: string;
    room: {
        net: number;
        tax: number;
        /**
         * Charged TIME units only (BE !1579): hours for hour plans, OPEN
         * days only for day plans — closed days are skipped, so this can
         * be smaller than the calendar span. Seats are deliberately a
         * separate factor: room.net = perUnitPrice × units × seats. Null
         * on breakdowns from BEs that don't ship it yet; the room-line
         * label then falls back to `details.period`.
         */
        units: number | null;
    };
    services: { net: number; tax: number; items: CustomerBreakdownItem[] };
    netTotal: number;
    taxTotal: number;
    grossTotal: number;
};

/**
 * Tax-inclusive room + service line amounts (native currency), so the
 * breakdown lines add up to the total the customer pays. `taxTotal` is a
 * booking-level figure that can exceed the per-line taxes, so it is spread
 * across the lines in proportion to their net (the booking's effective
 * rate) rather than summed from them; the room line takes the remainder so
 * the lines always total `grossTotal` exactly. Breakdowns without a usable
 * `grossTotal` fall back to each line's own net + tax.
 */
export const customerBreakdownGrossLines = (
    breakdown: CustomerBreakdown
): { room: number; items: number[] } => {
    const { room, services, netTotal, grossTotal } = breakdown;
    if (!(grossTotal > 0) || !(netTotal > 0)) {
        return {
            room: room.net + room.tax,
            items: services.items.map((item) => item.net + item.tax)
        };
    }
    const ratio = grossTotal / netTotal;
    const items = services.items.map((item) => item.net * ratio);
    return {
        room: grossTotal - items.reduce((sum, amount) => sum + amount, 0),
        items
    };
};

const readNumber =(v: unknown): number => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
};

export const normalizeCustomerBreakdown = (
    raw: unknown
): CustomerBreakdown | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const room = (r.room ?? {}) as Record<string, unknown>;
    const services = (r.services ?? {}) as Record<string, unknown>;
    const items = Array.isArray(services.items) ? services.items : [];
    return {
        currency: typeof r.currency === 'string' ? r.currency : '',
        room: {
            net: readNumber(room.net),
            tax: readNumber(room.tax),
            units: room.units != null ? readNumber(room.units) : null
        },
        services: {
            net: readNumber(services.net),
            tax: readNumber(services.tax),
            items: items
                .map((it) => {
                    if (!it || typeof it !== 'object') return null;
                    const i = it as Record<string, unknown>;
                    const id = i.serviceId ?? i.service_id;
                    if (id == null) return null;
                    return {
                        serviceId: String(id),
                        name: typeof i.name === 'string' ? i.name : '',
                        quantity: readNumber(i.quantity),
                        net: readNumber(i.net),
                        tax: readNumber(i.tax)
                    };
                })
                .filter((it): it is CustomerBreakdownItem => it !== null)
        },
        netTotal: readNumber(r.netTotal ?? r.net_total),
        taxTotal: readNumber(r.taxTotal ?? r.tax_total),
        grossTotal: readNumber(r.grossTotal ?? r.gross_total)
    };
};

/**
 * `details.quota` — live company-group booking-limit usage, returned only on
 * `POST /booking/quote/` responses and only for users whose company group has
 * an active quota. Null for everyone else, so a null quota means "no limit to
 * show", never "limit of zero".
 *
 * Quota is enforced SOFT on the quote (HTTP 200 either way) and HARD on
 * `POST /booking/` (HTTP 400) — `details.reasonToCheck` carries the BE's
 * human-readable warning when the booking being priced would breach it, and
 * is an empty string otherwise.
 *
 * `remainingAfterBook` is what would be left AFTER committing the booking
 * currently being quoted, so `limit - remainingAfterBook` is the projected
 * usage the checkout card previews. Both `remaining` fields can be negative
 * when the limit is already breached.
 */
export type BookingQuota = {
    /** Units already used in the current period. */
    consumed: number;
    /** Units still available before this booking. */
    remaining: number;
    /** Units that would still be available after this booking. */
    remainingAfterBook: number;
    /** Maximum units allowed per reset period. */
    limit: number;
    /** Unit the limit is counted in: `hour` | `day` | `month`. */
    unit: string;
    /** How often the allowance resets: `daily` | `monthly` | `annual` | `never`. */
    resetPeriod: string;
};

/**
 * The quote endpoint documents this block in snake_case while the rest of the
 * envelope rides DRF's camelCase renderer, and which one lands depends on the
 * BE version — so read both spellings rather than betting on either.
 */
export const normalizeBookingQuota = (raw: unknown): BookingQuota | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const limit = readNumber(r.limit);
    // A quota with no positive limit can't be drawn as a progress bar and
    // says nothing useful — treat it as "no quota" rather than rendering
    // an empty meter.
    if (limit <= 0) return null;
    const consumed = readNumber(r.consumed);
    const remaining = readNumber(r.remaining ?? limit - consumed);
    return {
        consumed,
        remaining,
        remainingAfterBook: readNumber(
            r.remainingAfterBook ?? r.remaining_after_book ?? remaining
        ),
        limit,
        unit: typeof r.unit === 'string' ? r.unit : 'day',
        resetPeriod:
            typeof r.resetPeriod === 'string'
                ? r.resetPeriod
                : typeof r.reset_period === 'string'
                  ? r.reset_period
                  : ''
    };
};

/**
 * Pull the quota block out of a quote response. Lives on `details` for the
 * pricing-engine (non-provider) shape; the provider path synthesizes its own
 * `details`, so the top level is checked too.
 */
export const readQuoteQuota = (quote: unknown): BookingQuota | null => {
    if (!quote || typeof quote !== 'object') return null;
    const q = quote as Record<string, unknown>;
    const details = (q.details ?? {}) as Record<string, unknown>;
    return normalizeBookingQuota(details.quota ?? q.quota);
};

/**
 * The BE's quota warning for the booking being quoted, or `null` when the
 * booking is within the limit. Non-empty means `POST /booking/` will reject
 * this booking with HTTP 400.
 */
export const readQuoteQuotaWarning = (quote: unknown): string | null => {
    if (!quote || typeof quote !== 'object') return null;
    const q = quote as Record<string, unknown>;
    const details = (q.details ?? {}) as Record<string, unknown>;
    const reason =
        details.reasonToCheck ??
        details.reason_to_check ??
        q.reasonToCheck ??
        q.reason_to_check;
    return typeof reason === 'string' && reason.trim() ? reason.trim() : null;
};

export type ProviderQuoteFields = {
    spacePriceReferenceToken?: string;
    expiresAt?: string;
    // When present, the provider requires the user to accept this EULA
    // before the create call. The FE renders an inline checkbox that links
    // to this URL; we send `tncAccepted: true` on create when checked.
    eulaUrl?: string;
    // PR #3 (Services Plan M2): services rolled into the quote total.
    // `servicesTotal` is GROSS (slot + services tax already inside it).
    servicesTotal?: number;
    servicesTaxTotal?: number;
    quotedServices?: QuotedService[];
    servicesDropped?: QuoteServiceDropped[];
    // BE !1630: the add-ons bookable on the slot this quote just priced —
    // the checkout picker's only feed. Empty when the quote-time hold could
    // not be placed, when add-ons are switched off, or on quotes from BEs
    // that predate it; the picker hides itself then. Money fields are in
    // the response currency (converted by the same factor as the lines).
    availableServices: AvailableService[];
    // Provider's native currency when the BE converted the quote for
    // display; null when no conversion happened. Pairs with
    // `currencyConversionRate` for the picker's "≈ AED 22 @ 0.272" hint.
    currencyOriginal?: string | null;
    // PR #13b: quote-time IWG temp reservation. All three are null when
    // the quote-time create failed (fail-soft); booking-create then mints
    // a fresh reservation as before. `reservationExpiresAt` is the BE
    // auto-release deadline (admin-configurable, default 10 min) — IWG's
    // own server-side expiry is 20 min, whichever fires first wins.
    reservationId?: string | null;
    reservationSlotId?: string | null;
    reservationExpiresAt?: string | null;
    // Exact API base the quote was fetched from. Releases must hit the
    // same host (the stage remote-config override can point quotes at a
    // different BE than NEXT_PUBLIC_API_URL) and must be buildable
    // synchronously inside unload handlers — so it rides on the quote
    // instead of being re-resolved (async) at release time.
    quoteApiBase?: string;
    // Itemized customer breakdown in the provider's native currency —
    // convert with `currencyConversionRate` for display. Null on quotes
    // from BEs that haven't shipped it yet.
    customerBreakdown?: CustomerBreakdown | null;
    currencyConversionRate?: number | null;
};

export type ProviderQuote = BookingCreate & ProviderQuoteFields;

/**
 * The wire is camelCase across the board — DRF's CamelCaseJSONRenderer rewrites
 * snake-cased model fields at render time. Keep this type aligned with what
 * the backend actually emits, not the Python field names.
 */
export type ProviderStatusPayload = {
    provider?: string | null;
    status?: string | null;
    externalBookingId?: string | null;
    bookingReferenceNumber?: string | null;
    availableServices?: unknown[];
    reservationExpiresAt?: string | null;
    cancellationFee?: number | null;
    cancellationFeeCurrency?: string | null;
};

export type ProviderBookingServiceStatus =
    | 'Requested'
    | 'Booked'
    | 'Cancelled'
    | 'Failed';

/**
 * Per-line `ProviderBookingService` row as returned by
 * `GET /booking/{id}/services/`. Decimal fields come over the wire as strings
 * to preserve precision — keep them as strings and format at render time.
 */
export type ProviderBookingServiceRow = {
    id: number;
    serviceId: string;
    externalServiceSaleId?: string | null;
    externalBookingServiceId?: string | null;
    name: string;
    categoryId?: string | null;
    categoryLabel?: string | null;
    quantity: number;
    unitPrice: string;
    taxAmount: string;
    currency: string;
    deliveryTime?: string | null;
    notes?: string | null;
    status: ProviderBookingServiceStatus;
    requestedAt?: string | null;
    bookedAt?: string | null;
    cancelledAt?: string | null;
    failureReason?: string | null;
    // PR #5 refund audit — populated on cancelled rows only.
    refundedAmountInBookingCurrency?: string | null;
    refundFxRateUsed?: string | null;
    // PR #7 unit-aware pricing.
    pricingUnitId?: string | null;
    pricingUnitLabel?: string | null;
};

/**
 * Envelope returned by `GET /booking/{id}/services/`. Non-provider bookings
 * still get a well-formed shape (`services: []`, both pending ids null) so
 * the FE can call unconditionally without branching on listing kind.
 *
 * Polling rule: keep polling while either `pendingAmendmentJobId` (M3 —
 * user-driven PUT) OR `pendingAttachJobId` (PR #8 — post-confirm settle)
 * is non-null. Both must be null before treating the panel as settled.
 */
export type BookingServicesResponse = {
    services: ProviderBookingServiceRow[];
    pendingAmendmentJobId: string | null;
    pendingAttachJobId: string | null;
};

/**
 * `source` enum from `POST /booking/{id}/cancel-preview/`. Drives the FE
 * UX — see PR #6 in `fe_impact_notes.md` for the per-source rendering
 * matrix.
 */
export type CancelPreviewSource =
    | 'iwg'                            // live IWG enquiry, just ran
    | 'iwg_cached'                     // within 10-min preview TTL
    | 'iwg_never_confirmed'            // PB had no external_booking_id
    | 'iwg_failed'                     // PB FAILED — full refund (or already issued)
    | 'iwg_error'                      // upstream IWG fault
    | 'iwg_timeout'                    // 30s sync poll timed out
    | 'confirmation_in_flight'         // PB.status == PENDING_CONFIRMATION
    | 'cancellation_in_flight'         // already cancelling, no preview
    | 'already_cancelled'              // 409 case
    | 'hotdesk_native'                 // no ProviderBooking row
    | 'provider_does_not_support_preview';

/**
 * `POST /booking/{id}/cancel-preview/` envelope. Always 200 except 409
 * (already cancelled). Decimal-valued fields come over the wire as
 * strings to preserve precision — keep as strings, parse at render.
 */
export type CancelPreviewResponse = {
    previewAvailable: boolean;
    source: CancelPreviewSource;
    fee: string | null;
    feeCurrency: string | null;
    bookingCurrency: string | null;
    expectedRefund: string | null;
    previewExpiresAt: string | null;
    reason: string | null;
};

/**
 * One bookable add-on. Since BE !1630 the checkout picker reads these off
 * `POST /booking/quote/` → `availableServices[]`: the slot-bound list IWG
 * returns on the quote-time hold, already priced for that exact slot. The
 * same row shape is what the deprecated (now always-empty)
 * `GET /listing/{id}/available-services/` catalogue used to produce, so
 * the amendment modal still types against it. Note `unitPrice` here is a
 * number, while the booked-row endpoint returns it as a string — different
 * BE serializers.
 */
export type AvailableService = {
    serviceId: string;
    name: string;
    invoiceDescription?: string | null;
    categoryId?: string | null;
    categoryLabel?: string | null;
    isVariablePrice: boolean;
    /** Catalogue-only flag — `null` on quote rows (the slot list has no counterpart). */
    allowedInShortNotice: boolean | null;
    /** Only the slot list carries this; `false` on the old catalogue rows. */
    requiresAdvanceNotice: boolean;
    currency: string;
    /** Net, per unit. Multiply by quantity for the line. */
    unitPrice: number;
    /** Per-unit tax (BE !1630). Zero on the old catalogue rows, which shipped net only. */
    taxAmount: number;
    /** Gross per unit (`unitPrice + taxAmount`); null when the BE didn't ship it. */
    totalPrice: number | null;
    /**
     * `null` on quote rows when IWG omits `price.unitId` on the slot payload
     * (seen on every payload so far — BE !1630). Null = no quantity cap;
     * default to 1 and let the user choose.
     */
    pricingUnitId?: string | null;
    pricingUnitLabel?: string | null;
    /**
     * `true` = the quantity must equal the booking's seat count. The BE
     * rewrites any other value at quote time (`quantityForcedToDelegates`
     * on the priced line), so the picker defaults to seats and locks the
     * stepper. Only the slot list carries this (BE !1630); the old catalogue
     * rows always had `null`.
     */
    mustMatchDelegates?: boolean | null;
    /**
     * IWG `ServiceDeliveryType` (BE !1583) — drives the delivery-time UI.
     * 0 "None": no delivery-time UI at all. 1 "Single": one optional time
     * per pick. 2 "Multiple": the same service may be ordered on several
     * lines with distinct times. This is IWG's complete enum (no 3+);
     * null on catalogues from BEs that don't ship it yet → treat as 0.
     */
    serviceDeliveryTypeId?: number | null;
    serviceDeliveryTypeLabel?: string | null;
};

/**
 * Envelope returned by the deprecated `GET /listing/{id}/available-services/`
 * (PR #13). `currencyOriginal` and `currencyConversionRate` are non-null
 * only when a conversion happened — branch on either for the inline rate
 * hint. Always empty since BE !1630.
 */
export type AvailableServicesResponse = {
    services: AvailableService[];
    currency: string | null;
    currencyOriginal: string | null;
    currencyConversionRate: number | null;
};

const normalizeAvailableService = (raw: unknown): AvailableService | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const id = readString(r.service_id ?? r.serviceId);
    if (!id) return null;
    const shortNotice = r.allowed_in_short_notice ?? r.allowedInShortNotice;
    const totalPrice = r.total_price ?? r.totalPrice;
    return {
        serviceId: id,
        name: typeof r.name === 'string' ? r.name : '',
        invoiceDescription: readString(
            r.invoice_description ?? r.invoiceDescription
        ),
        categoryId: readString(r.category_id ?? r.categoryId),
        categoryLabel: readString(r.category_label ?? r.categoryLabel),
        isVariablePrice: !!(r.is_variable_price ?? r.isVariablePrice),
        allowedInShortNotice: shortNotice == null ? null : !!shortNotice,
        requiresAdvanceNotice: !!(
            r.requires_advance_notice ?? r.requiresAdvanceNotice
        ),
        currency: typeof r.currency === 'string' ? r.currency : '',
        unitPrice: Number(r.unit_price ?? r.unitPrice ?? 0),
        taxAmount: Number(r.tax_amount ?? r.taxAmount ?? 0),
        totalPrice: totalPrice == null ? null : Number(totalPrice),
        pricingUnitId: readString(r.pricing_unit_id ?? r.pricingUnitId),
        pricingUnitLabel: readString(r.pricing_unit_label ?? r.pricingUnitLabel),
        mustMatchDelegates:
            r.must_match_delegates == null && r.mustMatchDelegates == null
                ? null
                : !!(r.must_match_delegates ?? r.mustMatchDelegates),
        serviceDeliveryTypeId:
            r.service_delivery_type_id == null &&
            r.serviceDeliveryTypeId == null
                ? null
                : Number(r.service_delivery_type_id ?? r.serviceDeliveryTypeId),
        serviceDeliveryTypeLabel: readString(
            r.service_delivery_type_label ?? r.serviceDeliveryTypeLabel
        )
    };
};

/**
 * Desired-state body for `PUT /booking/{id}/services/`. The FE submits the
 * full target list; BE diffs against currently-BOOKED rows and routes each
 * service through ADD / KEEP-UPDATE / DROP at IWG.
 */
export type ServicesAmendmentPayload = {
    services: Array<{
        serviceId: string;
        quantity: number;
        deliveryTime?: string;
        notes?: string;
    }>;
    notes?: string;
    locationNotes?: string;
};

/** Successful 202 envelope. */
export type ServicesAmendmentResponse = {
    jobId: string;
    addedPbsIds: number[];
    droppedPbsIds: number[];
    keptPbsIds: number[];
    dropped: Array<{ serviceId: string; reason: string }>;
};

export type ServicesAmendmentErrorKind =
    | 'validation'    // 400 — non-provider / non-amendable / malformed payload
    | 'forbidden'     // 403
    | 'declined'      // 402 — Stripe declined the off-session top-up
    | 'in_flight'     // 409 — amendment already running OR booking not CONFIRMED
    | 'transient'     // 502 — IWG/Stripe transient; safe to retry
    | 'unknown';

export class ServicesAmendmentError extends Error {
    kind: ServicesAmendmentErrorKind;
    status: number;
    body: Record<string, unknown>;
    transactionId?: number;
    constructor(
        kind: ServicesAmendmentErrorKind,
        status: number,
        body: Record<string, unknown>
    ) {
        super(
            typeof body?.detail === 'string'
                ? (body.detail as string)
                : `Service amendment failed (${status})`
        );
        this.kind = kind;
        this.status = status;
        this.body = body;
        const txId = body?.transaction_id ?? body?.transactionId;
        if (typeof txId === 'number') this.transactionId = txId;
    }
}

const classifyAmendmentStatus = (
    status: number
): ServicesAmendmentErrorKind => {
    if (status === 400) return 'validation';
    if (status === 402) return 'declined';
    if (status === 403) return 'forbidden';
    if (status === 409) return 'in_flight';
    if (status === 502) return 'transient';
    return 'unknown';
};

const normalizeCancelPreview = (raw: unknown): CancelPreviewResponse => {
    const r = (raw && typeof raw === 'object'
        ? (raw as Record<string, unknown>)
        : {}) as Record<string, unknown>;
    return {
        previewAvailable: !!(r.preview_available ?? r.previewAvailable),
        source: ((r.source as CancelPreviewSource) ||
            'provider_does_not_support_preview') as CancelPreviewSource,
        fee: readString(r.fee),
        feeCurrency: readString(r.fee_currency ?? r.feeCurrency),
        bookingCurrency: readString(r.booking_currency ?? r.bookingCurrency),
        expectedRefund: readString(r.expected_refund ?? r.expectedRefund),
        previewExpiresAt: readString(r.preview_expires_at ?? r.previewExpiresAt),
        reason: readString(r.reason)
    };
};

const readString = (v: unknown): string | null => {
    // The typed client turns `date-time` fields into `Date` instances, while
    // these stay ISO strings in the FE contract (consumers parse them with
    // dayjs). Re-serialize rather than falling through to `String(date)`,
    // which would hand back a locale-formatted date.
    if (v instanceof Date) {
        return Number.isNaN(v.getTime()) ? null : v.toISOString();
    }
    return typeof v === 'string' ? v : v == null ? null : String(v);
};

/**
 * Re-raise an SDK `ApiException` as the `{status, body}` shape the booking
 * surfaces already branch on, so the transport swap doesn't ripple out into
 * every caller. Non-API failures (network, upstream-HTML guard) pass through
 * untouched.
 */
const rethrowAsStatusError = (error: unknown): never => {
    if (error instanceof ApiException) {
        throw { status: error.code, body: error.body ?? {} };
    }
    throw error;
};

const normalizeBookingServiceRow = (raw: unknown): ProviderBookingServiceRow | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const idNum = Number(r.id);
    if (!Number.isFinite(idNum)) return null;
    return {
        id: idNum,
        serviceId: String(r.service_id ?? r.serviceId ?? ''),
        externalServiceSaleId: readString(
            r.external_service_sale_id ?? r.externalServiceSaleId
        ),
        externalBookingServiceId: readString(
            r.external_booking_service_id ?? r.externalBookingServiceId
        ),
        name: typeof r.name === 'string' ? r.name : '',
        categoryId: readString(r.category_id ?? r.categoryId),
        categoryLabel: readString(r.category_label ?? r.categoryLabel),
        quantity: Number(r.quantity ?? 0),
        unitPrice: String(r.unit_price ?? r.unitPrice ?? '0'),
        taxAmount: String(r.tax_amount ?? r.taxAmount ?? '0'),
        currency: typeof r.currency === 'string' ? r.currency : '',
        deliveryTime: readString(r.delivery_time ?? r.deliveryTime),
        notes: readString(r.notes),
        status: ((r.status as ProviderBookingServiceStatus) ||
            'Requested') as ProviderBookingServiceStatus,
        requestedAt: readString(r.requested_at ?? r.requestedAt),
        bookedAt: readString(r.booked_at ?? r.bookedAt),
        cancelledAt: readString(r.cancelled_at ?? r.cancelledAt),
        failureReason: readString(r.failure_reason ?? r.failureReason),
        refundedAmountInBookingCurrency: readString(
            r.refunded_amount_in_booking_currency ??
                r.refundedAmountInBookingCurrency
        ),
        refundFxRateUsed: readString(r.refund_fx_rate_used ?? r.refundFxRateUsed),
        pricingUnitId: readString(r.pricing_unit_id ?? r.pricingUnitId),
        pricingUnitLabel: readString(r.pricing_unit_label ?? r.pricingUnitLabel)
    };
};

/**
 * Typed quote-error codes (BE !1577). `POST /booking/quote/` 400 bodies are
 * `{available, code, detail}` — `code` classifies known IWG/Titan validation
 * failures with display-safe BE wording in `detail` (raw provider text stays
 * in BE logs). Unknown/absent codes fall back to the generic handling.
 */
export const QUOTE_ERROR_CODES = [
    'occupied',          // the specific space is already booked for that interval
    'not_enough_desks',  // Day Campus: desks free, but fewer than requested seats
    'capacity_exceeded', // seats exceed the space's capacity
    'invalid_time',      // end/start window rejected
    'invalid_date',      // date in the past
    'no_availability',   // nothing free
    'closed_days',       // BE !1579: every day in the range is a closed day
    'same_day_cutoff',   // BE !1581: today can't be booked anymore (<1 whole
                         // hour before the location closes)
    // Client-synthesised, NOT sent by the BE. The native over-booking guard
    // answers with a bare translated `detail` and no code, so it can't drive
    // the sidebar panel on its own — `isOverBookingError` recognises it and
    // maps it onto these. Split by unit because the remedy differs: a span
    // booking needs a different month, an hourly one a different time.
    'over_booked_span',
    'over_booked',
    // Also client-synthesised: the native seat guard answers with a bare
    // translated `detail` carrying the free-seat count and no code
    // (`isNoAvailableSeatsError`). Distinct from `not_enough_desks`, which is
    // the BE's own Day-Campus code.
    'not_enough_seats'
] as const;

export type QuoteErrorCode = (typeof QUOTE_ERROR_CODES)[number];

export const readQuoteErrorCode = (body: unknown): QuoteErrorCode | null => {
    if (!body || typeof body !== 'object') return null;
    const code = (body as { code?: unknown }).code;
    return typeof code === 'string' &&
        (QUOTE_ERROR_CODES as readonly string[]).includes(code)
        ? (code as QuoteErrorCode)
        : null;
};

export type CreateBookingErrorBody = {
    eulaRequired?: boolean;
    eulaUrl?: string;
    available?: boolean;
    detail?: string;
    [key: string]: unknown;
};

export class ProviderCreateError extends Error {
    status: number;
    body: CreateBookingErrorBody;
    retryAfterSeconds?: number;
    constructor(
        status: number,
        body: CreateBookingErrorBody,
        retryAfterSeconds?: number
    ) {
        super(
            typeof body?.detail === 'string'
                ? body.detail
                : `Provider create failed (${status})`
        );
        this.status = status;
        this.body = body;
        this.retryAfterSeconds = retryAfterSeconds;
    }
}

const normalizeQuotedService = (raw: unknown): QuotedService | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const id = String(r.service_id ?? r.serviceId ?? '');
    if (!id) return null;
    return {
        serviceId: id,
        name: typeof r.name === 'string' ? r.name : '',
        categoryId:
            typeof r.category_id === 'string'
                ? r.category_id
                : typeof r.categoryId === 'string'
                  ? r.categoryId
                  : null,
        categoryLabel:
            typeof r.category_label === 'string'
                ? r.category_label
                : typeof r.categoryLabel === 'string'
                  ? r.categoryLabel
                  : null,
        quantity: Number(r.quantity ?? 0),
        unitPrice: Number(r.unit_price ?? r.unitPrice ?? 0),
        currency: typeof r.currency === 'string' ? r.currency : '',
        taxAmount: Number(r.tax_amount ?? r.taxAmount ?? 0),
        deliveryTime:
            typeof r.delivery_time === 'string'
                ? r.delivery_time
                : typeof r.deliveryTime === 'string'
                  ? r.deliveryTime
                  : null,
        notes: typeof r.notes === 'string' ? r.notes : null,
        quantityCappedFrom:
            r.quantity_capped_from == null && r.quantityCappedFrom == null
                ? null
                : Number(r.quantity_capped_from ?? r.quantityCappedFrom),
        mustMatchDelegates:
            r.must_match_delegates == null && r.mustMatchDelegates == null
                ? null
                : !!(r.must_match_delegates ?? r.mustMatchDelegates),
        quantityForcedToDelegates:
            r.quantity_forced_to_delegates == null &&
            r.quantityForcedToDelegates == null
                ? null
                : !!(r.quantity_forced_to_delegates ?? r.quantityForcedToDelegates)
    };
};

const normalizeQuoteServicesDropped = (raw: unknown): QuoteServiceDropped[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((d) => {
            if (!d || typeof d !== 'object') return null;
            const r = d as Record<string, unknown>;
            return {
                serviceId: String(r.service_id ?? r.serviceId ?? ''),
                reason: String(r.reason ?? '')
            };
        })
        .filter((d): d is QuoteServiceDropped => d !== null && !!d.serviceId);
};

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const computeProviderQuotePeriod = (data: BookingQuotation): number => {
    const unit = (data.unit ?? '').toLowerCase();
    if (unit === 'month' || unit === 'annual') {
        return data.duration ?? 1;
    }
    const start = data.startDateTime
        ? new Date(data.startDateTime).getTime()
        : NaN;
    const end = data.endDateTime
        ? new Date(data.endDateTime).getTime()
        : NaN;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return 1;
    }
    if (unit === 'hour') {
        return Math.max(1, Math.round((end - start) / MS_PER_HOUR));
    }
    // Day plan: inclusive span. Floor, not round — the window now runs the
    // listing's working hours (open on the first day → close on the last),
    // so a 3-day booking on a 15h-a-day listing spans 2.6 days and rounding
    // would bill it as 4. Flooring the leftover part-day is exact for any
    // window shorter than 24h, and still correct for the legacy
    // midnight → midnight shape.
    return Math.max(1, Math.floor((end - start) / MS_PER_DAY) + 1);
};

/**
 * Provider quote responses are flat (no nested `details`). Synthesize the
 * BookingCreate `details` shape from the response + originating request so
 * the rest of the booking flow can read it uniformly.
 */
const buildProviderQuoteDetails = (
    json: Record<string, unknown>,
    data: BookingQuotation
): BookingCreate['details'] => {
    const finalPrice = Number(json?.totalPrice ?? json?.price ?? 0);
    const vat = Number(json?.taxAmount ?? json?.vat ?? 0);
    const netAmount = Number(
        json?.netAmount ?? json?.basePrice ?? (finalPrice - vat)
    );
    // BE's `netAmount` / `taxAmount` are the totals (slot + services). Strip
    // services out of `unitPrice` and `transactionValue` so the sidebar's
    // slot line displays the slot rate, not the slot-plus-services rate
    // (which would visually double-count when the sidebar also renders a
    // services line below). Services are zero on listings without picks.
    const servicesNet = Number(
        json?.services_total ?? json?.servicesTotal ?? 0
    );
    const slotNet = Math.max(0, netAmount - servicesNet);
    const seats = data.seats ?? 1;
    const period = computeProviderQuotePeriod(data);
    const divisor = Math.max(period * seats, 1);
    const unitPrice = slotNet / divisor;
    return {
        unit: data.unit,
        seats,
        period,
        timezone: data.timezone ?? '',
        startDateTime: data.startDateTime,
        endDateTime: data.endDateTime ?? data.startDateTime,
        paymentMethod: data.paymentMethod,
        // Thread the saved-card ids through when paying by credit card. The
        // BE validator gates these on payment_method === "credit_card"
        // (app/serializers/booking.py:238-245). Note the FE-to-BE rename:
        // PaymentDetails.cardCustomerId → BookingDetails.customerId.
        ...(data.paymentMethod === 'credit_card' &&
            data.paymentDetails && {
                creditCardId: data.paymentDetails.creditCardId,
                customerId: data.paymentDetails.cardCustomerId
            }),
        currency: typeof json?.currency === 'string' ? json.currency : undefined,
        finalPrice,                  // total gross (slot + services)
        vat,                          // total VAT (slot + services tax)
        unitPrice,                    // slot-only per-unit rate
        transactionValue: slotNet,    // slot-only net (services excluded)
        // Coupon preview (BE !1585) — `totalPrice` above is already
        // discounted; this carries the amount taken off. Stamped AFTER the
        // BE's currency conversion, so it's in the display currency —
        // unlike customerBreakdown, do NOT multiply by the conversion rate.
        discountValue: Number(json?.discountValue ?? json?.discount_value ?? 0)
    };
};

const normalizeProviderStatus = (
    raw: ProviderStatusPayload | ProviderStatus | null | undefined
) => {
    if (!raw) return undefined;
    const r = raw as ProviderStatusPayload;
    return {
        provider: r.provider ?? null,
        status: r.status ?? null,
        externalBookingId: r.externalBookingId ?? null,
        bookingReferenceNumber: r.bookingReferenceNumber ?? null,
        availableServices: r.availableServices ?? [],
        reservationExpiresAt: readString(r.reservationExpiresAt),
        cancellationFee:
            typeof r.cancellationFee === 'number'
                ? r.cancellationFee
                : r.cancellationFee
                  ? Number(r.cancellationFee)
                  : null,
        cancellationFeeCurrency: r.cancellationFeeCurrency ?? null
    };
};

const resolveApiUrl = async (): Promise<string> => {
    const base = process.env.NEXT_PUBLIC_API_URL || '';
    try {
        const overrideUrl = (await fetchRemoteConfig(
            RemoteConfigKeys.HD_TEST_API_URL_KEY,
            'string'
        )) as string;
        const overrideEnabled = (await fetchRemoteConfig(
            RemoteConfigKeys.HD_TEST_API_ENABLE_KEY,
            'boolean'
        )) as boolean;
        if (overrideEnabled && process.env.STAGE === 'stage' && overrideUrl) {
            return overrideUrl;
        }
    } catch {
        /* fall through */
    }
    return base;
};

/** Tags the request so `HttpClientLibrary` dispatches it with `keepalive`. */
const keepaliveMiddleware: Middleware = {
    pre: async (context: RequestContext) => {
        context.setHeaderParam(KEEPALIVE_HEADER, '1');
        return context;
    },
    post: async (context: ResponseContext) => context
};

/**
 * Standalone client config for the unload-safe release beacon, built without
 * `ApiConfigration.getApiConfig` on purpose: that resolves the base URL from
 * remote config, and nothing awaited during pagehide ever settles. The base
 * URL instead rides on the quote that minted the hold, so this is entirely
 * synchronous.
 */
const buildReleaseConfig = (
    apiBase: string,
    authToken?: string,
    languageCode?: string
): Configuration => {
    const accessToken = getCookie('accessToken') || authToken || '';
    const csrfToken = getCookie('ga') || '';
    return createConfiguration({
        baseServer: new ServerConfiguration(apiBase, {}),
        httpApi: new HttpClientLibrary(),
        authMethods: {
            ...(!!accessToken && {
                Bearer: {
                    tokenProvider: {
                        getToken: () => String(accessToken),
                        getCSRF: () => String(csrfToken)
                    }
                }
            })
        },
        // The shared middleware also stamps the per-browser device id on this
        // route, which is what lets the BE verify the releaser is the quote's
        // device even when the hold was minted anonymously.
        promiseMiddleware: [
            myPromiseMiddleware(languageCode || 'en'),
            keepaliveMiddleware
        ]
    });
};

/** Handles identifying one quote-time IWG temp reservation for release. */
export type HeldReservation = {
    reservationId: string;
    priceToken: string | null;
    apiBase: string | null;
};

/**
 * Fire the quote-release `DELETE /booking/quote/?reservationId=...[&token=...]`
 * for a held IWG temp reservation.
 *
 * Identify the hold by `reservationId` (BE-preferred): IWG mints the same
 * `spacePriceReferenceToken` for repeated quotes of the same room, so
 * releasing by token alone risks freeing the wrong hold. The optional
 * `priceToken` lets the BE evict the matching price-cache entry in the
 * same call. Idempotent + fail-soft on the BE — 204 even for unknown ids.
 *
 * Built for unload safety: the client config is assembled synchronously from
 * the quote's own API base (no awaits — `resolveApiUrl`'s remote-config reads
 * would never finish during pagehide), so the only thing between the call and
 * the dispatch is the SDK's promise chain, which resolves on microtasks and
 * therefore still drains before the page is discarded. The request rides
 * `keepalive: true` so it survives the teardown itself. This matters most on
 * login, which ends in a full `window.location.reload()` (AuthService.login)
 * while an anonymous quote's reservation may still be held.
 *
 * Fires on real abandonment only (unmount / pagehide / mount sweep) —
 * re-quote supersession is handled BE-side via the X-Device-Id header on
 * the quote POST. Returns the fetch promise so callers that care can
 * await BE completion; unload callers just drop it. Returns null when
 * nothing dispatchable (no id / no API base).
 */
export const dispatchReservationRelease = (
    held: HeldReservation | null,
    authToken?: string,
    languageCode?: string
): Promise<unknown> | null => {
    if (!held?.reservationId) return null;
    const apiBase = held.apiBase || process.env.NEXT_PUBLIC_API_URL || '';
    if (!apiBase) return null;
    try {
        const api = new BookingApi(
            buildReleaseConfig(apiBase, authToken, languageCode)
        );
        return api
            .bookingQuoteRelease(
                held.reservationId,
                held.priceToken ?? undefined
            )
            .catch(() => undefined);
    } catch {
        /* fail-soft — BE's scheduled release + IWG's 20-min expiry catch it */
        return null;
    }
};

export class BookingService {
    bookingApi!: BookingApi;
    /** The IWG service catalogue lives on the listing resource. */
    listingApi!: ListingApi;
    constructor(apiConfig: Configuration) {
        this.bookingApi = new BookingApi(apiConfig);
        this.listingApi = new ListingApi(apiConfig);
    }

    /**
     * Quote for non-provider listings, where the BE answers with the
     * booking-create envelope (nested `details`) rather than the flat provider
     * shape. `BookingQuoteResponse` models both, so the cast narrows to the
     * branch this path always gets — and is what lets the result be handed
     * straight to `createBooking`, as the legacy flow does.
     */
    getBookingQuote(data: BookingQuotation): Promise<BookingCreate> {
        try {
            return this.bookingApi.bookingQuote(data) as unknown as Promise<BookingCreate>;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Quote for provider (IWG/Regus) listings. The endpoint answers with a
     * flat provider payload rather than the booking-create envelope, so the
     * response is reshaped here into the `details` form the rest of the
     * booking flow reads.
     *
     * `configurationTypeId` is an IWG Meeting Room layout pick; omit (don't
     * send null/0) when the user hasn't selected an alternate so the BE
     * uses the listing's default.
     */
    async getProviderBookingQuote(
        data: BookingQuotation,
        configurationTypeId?: number,
        services?: PickedService[]
    ): Promise<ProviderQuote> {
        // Resolved (not fetched) here purely to stamp `quoteApiBase` on the
        // result: the release beacon has to rebuild a client synchronously
        // against the exact host this quote came from.
        const apiBase = await resolveApiUrl();
        // Build the body progressively: only include optional fields when set
        // so the BE sees the exact same payload it would without the picker
        // when nothing's selected.
        const servicesBody =
            Array.isArray(services) && services.length > 0
                ? {
                      services: services.map((s) => ({
                          serviceId: s.serviceId,
                          quantity: s.quantity,
                          ...(s.notes && { notes: s.notes }),
                          ...(s.deliveryTime && {
                              deliveryTime: s.deliveryTime
                          })
                      }))
                  }
                : {};
        const body: BookingQuotation = {
            ...data,
            ...(typeof configurationTypeId === 'number' &&
                configurationTypeId > 0 && { configurationTypeId }),
            ...servicesBody
        };
        // The per-browser id that outlives login/logout — which lets the BE
        // tie this quote to the create/release that follows even across the
        // auth boundary — is stamped on this route by the shared middleware.
        const json = await this.bookingApi
            .bookingQuote(body)
            .catch(rethrowAsStatusError);
        // Provider quote endpoint returns a flat shape (totalPrice, taxAmount,
        // netAmount, currency, ...) instead of the BookingCreate shape with a
        // nested `details`. Synthesize `details` from the response + request
        // so downstream consumers can read it uniformly.
        const synthesizedDetails = buildProviderQuoteDetails(
            json as unknown as Record<string, unknown>,
            data
        );
        // PR #3 service fields on the quote response. `servicesTotal` is the
        // sum of (unitPrice × quantity) for accepted picks; the BE has already
        // rolled it into `totalPrice`, so don't double-count when reading.
        const quotedServices: QuotedService[] = (json.services ?? [])
            .map(normalizeQuotedService)
            .filter((s: QuotedService | null): s is QuotedService => s !== null);
        // BE !1630: the picker feed rides on the quote. Same row shape the
        // catalogue endpoint used to return, so one normaliser serves both.
        // The generated client doesn't know the field yet — read it raw.
        const rawAvailable = (json as unknown as Record<string, unknown>)
            .availableServices;
        const availableServices: AvailableService[] = (
            Array.isArray(rawAvailable) ? rawAvailable : []
        )
            .map(normalizeAvailableService)
            .filter((s): s is AvailableService => s !== null);
        const currencyOriginal = (json as unknown as Record<string, unknown>)
            .currencyOriginal;
        return {
            ...(json as unknown as BookingCreate),
            listing: data.listing,
            details: synthesizedDetails,
            spacePriceReferenceToken: json.spacePriceReferenceToken,
            // Typed as `Date` by the client; the FE contract is an ISO string.
            expiresAt: readString(json.expiresAt) ?? undefined,
            eulaUrl: json.eulaUrl,
            servicesTotal: json.servicesTotal,
            servicesTaxTotal: json.servicesTaxTotal,
            quotedServices,
            servicesDropped: normalizeQuoteServicesDropped(json.servicesDropped),
            availableServices,
            currencyOriginal:
                typeof currencyOriginal === 'string' ? currencyOriginal : null,
            // PR #13b: quote-time temp reservation handles.
            reservationId: readString(json.reservationId),
            reservationSlotId: readString(json.reservationSlotId),
            reservationExpiresAt: readString(json.reservationExpiresAt),
            quoteApiBase: apiBase,
            customerBreakdown: normalizeCustomerBreakdown(
                json.customerBreakdown
            ),
            currencyConversionRate:
                json.currencyConversionRate != null
                    ? Number(json.currencyConversionRate)
                    : null
        };
    }

    createBooking(data: BookingCreate): Promise<Booking> {
        try {
            return this.bookingApi.bookingCreate(data);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Create for provider (IWG/Regus) listings — carries the provider-only
     * extras in `details`
     * (spacePriceReferenceToken, tncAccepted, requestedServices). Errors
     * include `eulaRequired`, 410 (expired token), 409 (conflict), 503
     * (transient).
     */
    async createProviderBooking(
        data: BookingCreate,
        extras: {
            spacePriceReferenceToken?: string;
            tncAccepted?: boolean;
            requestedServices?: unknown[];
            // IWG Meeting Room layout — must match the layout the
            // `spacePriceReferenceToken` was issued for. Rides on the top-level
            // envelope (not `details`) per BE spec.
            configurationTypeId?: number;
        }
    ): Promise<Booking> {
        const payload = {
            ...data,
            ...(typeof extras.configurationTypeId === 'number' &&
                extras.configurationTypeId > 0 && {
                    configurationTypeId: extras.configurationTypeId
                }),
            details: {
                // BookingSerializer.validate_details requires
                // currency/timezone/unit/period/startDateTime/endDateTime/
                // paymentMethod for both provider and non-provider listings.
                // Carry the synthesized quote details so validation passes,
                // then layer the provider-only extras on top.
                ...data.details,
                ...(extras.spacePriceReferenceToken && {
                    spacePriceReferenceToken: extras.spacePriceReferenceToken
                }),
                ...(typeof extras.tncAccepted === 'boolean' && {
                    tncAccepted: extras.tncAccepted
                }),
                requestedServices: extras.requestedServices ?? []
            }
        };
        // Carries the same per-browser id the quote did (stamped by the shared
        // middleware) — the BE matches them up even when the quote was minted
        // pre-login.
        const json = await this.bookingApi
            .bookingCreate(payload)
            .catch((error: unknown) => {
                if (error instanceof ApiException) {
                    // Retry-After separates the retryable cache-hiccup 503
                    // from the feature-off one; header names arrive
                    // lower-cased from the fetch layer.
                    const retryAfter =
                        error.headers?.['retry-after'] ??
                        error.headers?.['Retry-After'];
                    throw new ProviderCreateError(
                        error.code,
                        (error.body ?? {}) as CreateBookingErrorBody,
                        retryAfter ? Number(retryAfter) : undefined
                    );
                }
                throw error;
            });
        return json as Booking;
    }

    /**
     * Lightweight provider-status polling endpoint. Returns the provider
     * status dict for a booking (or `undefined` for non-provider). Backend
     * returns the dict unwrapped — same shape as `booking.providerStatus`.
     */
    async getProviderStatus(
        bookingId: number
    ): Promise<ReturnType<typeof normalizeProviderStatus>> {
        const payload = await this.bookingApi
            .bookingProviderStatus(bookingId)
            .catch(rethrowAsStatusError);
        return normalizeProviderStatus(payload);
    }

    /**
     * @deprecated BE !1630 — `GET /listing/{id}/available-services/` now
     * always returns `services: []`. IWG never provisioned the catalogue
     * endpoint behind it in production, so the checkout picker reads
     * `availableServices` off the quote instead (`getProviderBookingQuote`).
     * Kept only for the post-booking amendment modal until the BE ships a
     * replacement feed for it.
     *
     * `currency` (PR #13) — when supplied, the BE converts every price to
     * that currency using the same `CurrencyHelper` instance the quote
     * endpoint uses. The envelope's `currencyOriginal` /
     * `currencyConversionRate` are non-null only when a conversion happened.
     */
    async getAvailableServices(
        listingId: number,
        currency?: string
    ): Promise<AvailableServicesResponse> {
        const envelope = await this.listingApi
            .listingAvailableServices({
                id: listingId,
                // Omit entirely (not "") when unset, so the BE prices in the
                // listing's own currency instead of attempting a conversion.
                currency:
                    currency && currency.trim().length > 0
                        ? currency
                        : undefined
            })
            .catch(rethrowAsStatusError);
        const rawList = Array.isArray(envelope?.services)
            ? envelope.services
            : [];
        const services = rawList
            .map(normalizeAvailableService)
            .filter((s): s is AvailableService => s !== null);
        const rate = envelope?.currencyConversionRate;
        return {
            services,
            currency:
                typeof envelope?.currency === 'string'
                    ? envelope.currency
                    : null,
            currencyOriginal:
                typeof envelope?.currencyOriginal === 'string'
                    ? envelope.currencyOriginal
                    : null,
            currencyConversionRate:
                typeof rate === 'number'
                    ? rate
                    : rate == null
                      ? null
                      : Number(rate)
        };
    }

    /**
     * Amend the per-line services on a confirmed booking —
     * `PUT /booking/{id}/services/`. Body is the full desired state; BE
     * diffs against current BOOKED rows. Returns 202 with the job id +
     * affected PBS ids. Errors throw `ServicesAmendmentError` with a
     * classified `kind` so callers can branch UX without re-inspecting
     * status codes.
     */
    async updateBookingServices(
        bookingId: number,
        payload: ServicesAmendmentPayload
    ): Promise<ServicesAmendmentResponse> {
        // Wire shape is camelCase — the BE's global CamelCaseJSONParser
        // underscoreizes it (nested items included) before the controller
        // reads `service_id` / `location_notes`.
        const body = {
            services: payload.services.map((s) => ({
                serviceId: s.serviceId,
                quantity: s.quantity,
                ...(s.deliveryTime && { deliveryTime: s.deliveryTime }),
                ...(s.notes && { notes: s.notes })
            })),
            ...(payload.notes && { notes: payload.notes }),
            ...(payload.locationNotes && { locationNotes: payload.locationNotes })
        };
        const json = await this.bookingApi
            .bookingServicesUpdate(bookingId, body)
            .catch((error: unknown) => {
                if (error instanceof ApiException) {
                    throw new ServicesAmendmentError(
                        classifyAmendmentStatus(error.code),
                        error.code,
                        (error.body ?? {}) as Record<string, unknown>
                    );
                }
                throw error;
            });
        const dropped = Array.isArray(json.dropped) ? json.dropped : [];
        return {
            jobId: String(json.jobId ?? ''),
            addedPbsIds: Array.isArray(json.addedPbsIds) ? json.addedPbsIds : [],
            droppedPbsIds: Array.isArray(json.droppedPbsIds)
                ? json.droppedPbsIds
                : [],
            keptPbsIds: Array.isArray(json.keptPbsIds) ? json.keptPbsIds : [],
            dropped: dropped
                .map((d) => {
                    if (!d || typeof d !== 'object') return null;
                    const r = d as Record<string, unknown>;
                    return {
                        serviceId: String(r.service_id ?? r.serviceId ?? ''),
                        reason: String(r.reason ?? '')
                    };
                })
                .filter((d): d is { serviceId: string; reason: string } => d !== null)
        };
    }

    /**
     * Pre-cancel fee preview — `POST /booking/{id}/cancel-preview/`. Always
     * 200 except 409 (already cancelled). Idempotent: a fresh `source="iwg"`
     * preview caches on the PB for 10 min and a follow-up cancel PATCH within
     * the window reuses it without re-hitting IWG.
     *
     * Throws an object `{status, body}` on non-2xx (including 409) so callers
     * can branch on the already-cancelled case.
     */
    async getCancelPreview(
        bookingId: number
    ): Promise<CancelPreviewResponse> {
        const preview = await this.bookingApi
            .bookingCancelPreview(bookingId)
            .catch(rethrowAsStatusError);
        return normalizeCancelPreview(preview);
    }

    /**
     * Per-booking services list — `GET /booking/{id}/services/`. Returns
     * `ProviderBookingService` rows + the two pending-job ids (M3 amendment
     * + PR #8 post-confirm attach). Non-provider bookings get a well-formed
     * empty envelope, so callers can read unconditionally.
     */
    async getBookingServices(
        bookingId: number
    ): Promise<BookingServicesResponse> {
        const envelope = await this.bookingApi
            .bookingServicesRead(bookingId)
            .catch(rethrowAsStatusError);
        const rawList = Array.isArray(envelope?.services)
            ? envelope.services
            : [];
        return {
            services: rawList
                .map(normalizeBookingServiceRow)
                .filter((r): r is ProviderBookingServiceRow => r !== null),
            pendingAmendmentJobId: readString(envelope?.pendingAmendmentJobId),
            pendingAttachJobId: readString(envelope?.pendingAttachJobId)
        };
    }

    getBookingById(id: number): Promise<ReadOnlyBooking> {
        try {
            return this.bookingApi.bookingRead(id);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Booking read that also surfaces `details.customerBreakdown` and the
     * conversion rate as first-class fields, so callers don't each have to dig
     * them out of `details`.
     */
    async getBookingWithBreakdown(
        bookingId: number
    ): Promise<{
        booking: ReadOnlyBooking;
        customerBreakdown: CustomerBreakdown | null;
        currencyConversionRate: number | null;
    }> {
        const json = (await this.bookingApi
            .bookingRead(bookingId)
            .catch(rethrowAsStatusError)) as unknown as Record<string, unknown>;
        const details = (json.details ?? {}) as Record<string, unknown>;
        const rate =
            details.currencyConversionRate ?? details.currency_conversion_rate;
        return {
            booking: json as unknown as ReadOnlyBooking,
            customerBreakdown: normalizeCustomerBreakdown(
                details.customerBreakdown ?? details.customer_breakdown
            ),
            currencyConversionRate: rate != null ? Number(rate) : null
        };
    }

    updateBooking(bookingId: number, booking: Booking): Promise<Booking> {
        try {
            return this.bookingApi.bookingUpdate(bookingId, booking);
        } catch (error) {
            throw error;
        }
    }
    bookingList(filters = {}): Promise<InlineResponse2001> {
        try {
            return this.bookingApi.bookingList(filters);
        } catch (error) {
            throw error;
        }
    }
    /**
     * `timezone` is `booking.details.timezone`. It exists solely to reach
     * `getRemainingTime`, which needs it to read an offset-less stored value as
     * the listing's wall clock instead of as UTC — see `getSelectedTab`.
     */
    checkDateTimeStatus(
        endDate?: Date | string,
        isCheckinEnabled = true,
        timezone?: string | null
    ) {
        let validTill: number | undefined = undefined;
        let validUnit = '';
        let isEndSoon = false;
        let isLateChecking = false;
        const {
            days = 0,
            months = 0,
            hours = 0,
            minutes = 0
        } = getRemainingTime(endDate, undefined, timezone);
        switch (true) {
            case months >= 1:
                validTill = Math.floor(months);
                validUnit = getUnitText(validTill, 'month');
                break;
            case days >= 1:
                validTill = Math.floor(days);
                validUnit = getUnitText(validTill, 'day');
                break;
            case hours >= 1:
                validTill = Math.floor(hours);
                validUnit = getUnitText(validTill, 'hour');
                break;
            case minutes >= 1:
                validTill = Math.floor(minutes);
                validUnit = getUnitText(validTill, 'minute');
                break;
        }
        if (Math.floor(hours) <= 1) {
            isEndSoon = true;
        }
        if (validTill === undefined && !!isCheckinEnabled) {
            switch (true) {
                case months <= -1:
                    validTill = Math.floor(-1 * months);
                    validUnit = getUnitText(validTill, 'month-late');
                    break;
                case days <= -1:
                    validTill = Math.floor(-1 * days);
                    validUnit = getUnitText(validTill, 'day-late');
                    break;
                case hours <= -1:
                    validTill = Math.floor(-1 * hours);
                    validUnit = getUnitText(validTill, 'hour-late');
                    break;
                case minutes <= -1:
                    validTill = Math.floor(-1 * minutes);
                    validUnit = getUnitText(validTill, 'minute-late');
                    break;
            }
            if (!!validTill) {
                isLateChecking = true;
            }
        }

        return {
            isLateChecking,
            validUnit,
            validTill,
            isEndSoon
        };
    }
}
